import * as React from 'react'
import { RouteComponentProps, withRouter, Prompt } from 'react-router'
import { connect, Dispatch } from 'react-redux'
import { ApolloClient, ApolloQueryResult } from 'apollo-client'
import { NormalizedCache } from 'apollo-cache-inmemory'

import { Action } from '../../actions/types'
import {
  ProjectType,
  CaseInput,
  SAVE_PROJECT_DATA
} from '../Projects/projectQueries'
import { UserInfo, Store, getUserInfo } from '../../reducers'
import {
  EditableCase,
  getActiveCase,
  getProjectLocation,
  getProjectName,
  getActiveSpaceType,
  getProjectDirty,
  getAllCases
} from '../../reducers/project'
import { WorkspaceToolbar, ToolbarButton } from '../Workspace/WorkspaceStyles'
import { NewProjectDialog } from '../Projects'
import {
  showLoginForm,
  showNotification,
  hideNotification
} from '../../actions/ui_actions'
import { loadProject } from '../../actions/project'
import CurrentProjectDisplay from '../Layout/CurrentProjectDisplay'
import { CountryType, withCountries } from '../Shared/CountryOptions'
import { compose } from 'react-apollo'
import { saveThumbnail } from './thumbnail'
import { clearJobId } from '../../actions/calculation'
import { recordEvent } from '../../analytics'
import Error from '../Shared/Alerts/Error'

type SaveProjectDataResponse = ApolloQueryResult<{
  saveProjectData: ProjectType
}>

type ToolbarProps = {
  user: UserInfo
  cases: EditableCase[]
  activeCase: EditableCase | undefined
  dirty: boolean
  activeSpaceType: string
  projectLocation: string
  showLogin: () => Action
  loadProject: (project: ProjectType) => Action
  showNotification: (msg: string) => Action
  hideNotification: () => Action
  clearJob: () => Action
  countries: CountryType[]
  projectName: string
} & RouteComponentProps<{ projectId?: string }>

type ToolbarState = {
  saveProject: boolean
  newProject: boolean
  error: string | undefined
}

const prompt = location => ({ pathname }) =>
  pathname === location.pathname
    ? true
    : 'You have unchanged edits that will not be saved if you leave the page'

/* TODO: refactor this to the actual toolbar component and a data component that has the
   save etc. functionality. */
class ProjectToolbar extends React.Component<ToolbarProps, ToolbarState> {
  static contextTypes = {
    client: () => null
  }
  client: ApolloClient<NormalizedCache>

  // tslint:disable-next-line
  constructor(props: ToolbarProps, context?: any) {
    super(props)

    this.state = { saveProject: false, newProject: false, error: undefined }

    this.client = context.client
    this.saveProject = this.saveProject.bind(this)
    this.openProject = this.openProject.bind(this)
    this.startNew = this.startNew.bind(this)
  }

  openProject() {
    if (!this.props.user) {
      this.props.showLogin()
      return
    }
    this.props.clearJob()
    this.props.history.push('/projects')
  }

  showSaveNotification() {
    this.props.showNotification('Project successfully saved')
    window.setTimeout(this.props.hideNotification, 1700)
  }

  createThumbnail(kase: EditableCase | undefined) {
    if (!kase) {
      return
    }

    const elem = document.getElementById(
      'previz-canvas'
    ) as HTMLCanvasElement | null
    if (!elem) {
      return
    }

    saveThumbnail(kase.id, elem)
  }

  async saveProject() {
    if (!this.props.user) {
      this.props.showLogin()
      return
    }

    this.props.clearJob()

    if (this.props.match.params.projectId === 'new') {
      this.setState({ saveProject: true })
      return
    }

    // We ignore the result (we want to save the project even if the thumbnail cannot
    // be saved) but we still wait for this to complete so that saveProjectData mutation
    // returns the latest thumbnail image (if this finished after the saveProjectData
    // mutation, we'd get stale data).
    // TODO: rather than waiting here, just go ahead and let the upload finish later. If the
    // thumbnail image is ~30kb, the upload may take a while if the user relies on poor
    // connection. We can always use GraphQL writeData() etc. to update the thubnail later.
    await this.createThumbnail(this.props.activeCase)

    const cases: CaseInput[] = this.props.cases.map(editable => {
      return {
        id: editable.id,
        name: editable.name,
        description: editable.description,
        new: editable._new,
        delete: editable._deleted,
        data: JSON.stringify({
          touchedCategories: editable.touchedCategories,
          activeItem: editable.activeItem,
          loads: editable.loadCases,
          beams: editable.beams,
          slabs: editable.slabs
        })
      }
    })

    this.client
      .mutate({
        mutation: SAVE_PROJECT_DATA,
        variables: {
          projectId: this.props.match.params.projectId,
          activeCase: this.props.activeCase ? this.props.activeCase.id : null,
          cases
        }
      })
      .then(({ data: { saveProjectData } }: SaveProjectDataResponse) => {
        recordEvent('project', 'saved')
        this.showSaveNotification()
        this.props.loadProject(saveProjectData)
      })
      .catch(err => {
        // TODO: set save error
        console.log(err)
        this.setState({ error: 'Error saving project.\n' + err.message })
        console.error('error saving project', err)
      })
  }

  startNew() {
    if (!this.props.user) {
      this.props.showLogin()
      return
    }

    this.props.clearJob()
    this.setState({ newProject: true })
  }

  render() {
    const {
      projectLocation,
      projectName,
      dirty,
      cases,
      activeCase,
      activeSpaceType,
      countries = []
    } = this.props
    const country = countries.find(c => c.id === projectLocation)

    const activeBeam =
      activeCase && activeCase.beams ? activeCase.beams[0] : null

    return (
      <WorkspaceToolbar>
        <div className="toolbar-header">MY PROJECT</div>
        <ToolbarButton onClick={this.startNew}>+New</ToolbarButton>
        <ToolbarButton onClick={this.openProject}>Open</ToolbarButton>
        <ToolbarButton onClick={this.saveProject}>Save</ToolbarButton>
        <CurrentProjectDisplay />

        {/* In future, we could be a bit smarter about this but for now let user
              navigate away when the save project dialog is away (the save will
              cause a direct to projects/<id>/workspace which will trigger the modal
              otherwise - which we don't want). */}
        {!this.state.saveProject && (
          <Prompt message={prompt(location)} when={dirty} />
        )}
        {this.state.error && (
          <Error onDismiss={() => this.setState({ error: undefined })}>
            {this.state.error}
          </Error>
        )}
        <div>
          <NewProjectDialog
            isOpen={this.state.saveProject || this.state.newProject}
            closeDialog={() =>
              this.setState({ saveProject: false, newProject: false })
            }
            cases={this.state.newProject ? [] : cases}
            spaceType={activeSpaceType}
            initValues={{
              name: this.state.newProject ? '' : projectName,
              spaceType: activeSpaceType,
              location: projectLocation,
              fireResistance: activeBeam
                ? activeBeam.fireResistance
                : undefined,
              beamConcreteGrade: activeBeam
                ? activeBeam.concreteGrade
                : undefined
            }}
            projectLocation={country ? country.id : undefined}
          />
        </div>
      </WorkspaceToolbar>
    )
  }
}

const mapStateToProps = (state: Store.Complete) => ({
  cases: getAllCases(state),
  activeCase: getActiveCase(state),
  user: getUserInfo(state),
  projectLocation: getProjectLocation(state),
  projectName: getProjectName(state),
  dirty: getProjectDirty(state),
  activeSpaceType: getActiveSpaceType(state)
})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  showLogin: () => dispatch(showLoginForm('select')),
  loadProject: (project: ProjectType) => dispatch(loadProject(project)),
  showNotification: (msg: string) => dispatch(showNotification(msg)),
  hideNotification: () => dispatch(hideNotification()),
  clearJob: () => dispatch(clearJobId())
})

export default compose(
  withRouter,
  withCountries,
  connect(mapStateToProps, mapDispatchToProps)
)(ProjectToolbar)



// WEBPACK FOOTER //
// ./src/Components/ProjectToolbar/index.tsx